import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Download, Loader2 } from "lucide-react";
import html2pdf from "html2pdf.js";
import { cv } from "../data/content";
import { TinyStar } from "../components/Stickers";
import { buildCvPdfHtml } from "../lib/cvPdfTemplate";

export default function CV() {
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleDownload = async () => {
    if (generating) return;
    setGenerating(true);

    // Build an isolated container with the static PDF markup, append to body
    const container = document.createElement("div");
    container.id = "cv-pdf-container";
    container.style.cssText =
      "position:fixed;left:0;top:0;width:740px;z-index:-1;opacity:0;pointer-events:none;background:#ffffff;";
    container.innerHTML = buildCvPdfHtml();
    document.body.appendChild(container);

    try {
      // Wait one frame so the DOM lays out
      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => requestAnimationFrame(() => r()));

      await html2pdf()
        .set({
          margin: [6, 6, 6, 6],
          filename: `קורות חיים - ${cv.profile.name}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            letterRendering: true,
            windowWidth: 740,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            compress: true,
          },
          pagebreak: {
            mode: ["avoid-all", "css"],
            avoid: ["section", "li", "div"],
          },
        })
        .from(container.firstElementChild)
        .save();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PDF generation failed", e);
      alert("יצירת ה-PDF נכשלה. נסי שוב.");
    } finally {
      container.remove();
      setGenerating(false);
    }
  };

  return (
    <main
      data-testid="cv-page"
      className="mx-auto max-w-[1100px] px-5 md:px-10 lg:px-14 pt-16 md:pt-24 pb-24"
    >
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <span className="tag-pill">קורות חיים · CV</span>
        <button
          type="button"
          onClick={handleDownload}
          disabled={generating}
          data-testid="cv-download-btn"
          className="inline-flex items-center gap-2 bg-[color:var(--ink)] text-[color:var(--paper)] px-5 py-2.5 rounded-full font-display font-bold text-sm md:text-base hover:bg-[color:var(--pink)] transition-colors group disabled:opacity-60"
        >
          {generating ? (
            <Loader2 size={16} strokeWidth={2.5} className="animate-spin" />
          ) : (
            <Download
              size={16}
              strokeWidth={2.5}
              className="transition-transform group-hover:translate-y-0.5"
            />
          )}
          {generating ? "מכינה PDF…" : "לחצו להורדה"}
        </button>
      </div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="cv-name"
        className="font-display font-black tracking-tight leading-[0.9] text-6xl md:text-8xl"
      >
        {cv.profile.name}
        <span className="text-[color:var(--pink)]">.</span>
      </motion.h1>

      <p
        data-testid="cv-role"
        className="mt-4 font-serif-editorial italic text-2xl md:text-3xl text-[color:var(--charcoal)]"
      >
        {cv.profile.role}
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3 md:gap-4">
        <a
          href={`tel:${cv.profile.phone.replace(/-/g, "")}`}
          data-testid="cv-phone"
          className="inline-flex items-center gap-2 ink-border px-4 py-2.5 rounded-full font-medium hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] hover:border-[color:var(--pink)] transition-colors"
        >
          <Phone size={16} strokeWidth={2} />
          <span dir="ltr">{cv.profile.phone}</span>
        </a>
        <a
          href={`mailto:${cv.profile.email}`}
          data-testid="cv-email"
          className="inline-flex items-center gap-2 ink-border px-4 py-2.5 rounded-full font-medium hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] hover:border-[color:var(--pink)] transition-colors"
        >
          <Mail size={16} strokeWidth={2} />
          <span>{cv.profile.email}</span>
        </a>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        data-testid="cv-summary"
        className="mt-16 md:mt-20"
      >
        <div className="flex items-center gap-3 mb-5">
          <h2 className="font-display font-black text-3xl md:text-4xl">
            {cv.summary.title}
          </h2>
          <TinyStar className="w-4 h-4" color="#FF1493" />
        </div>
        <p className="text-lg md:text-xl leading-[1.75] max-w-3xl text-[color:var(--ink)]">
          {cv.summary.text}
        </p>
      </motion.section>

      <div className="mt-16 md:mt-20 space-y-16 md:space-y-20">
        {cv.sections.map((section, i) => (
          <motion.section
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
            data-testid={`cv-section-${i}`}
          >
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-display font-black text-3xl md:text-4xl">
                {section.title}
              </h2>
              <TinyStar className="w-4 h-4" color="#FF1493" />
            </div>

            <div className="divide-y divide-black/15 border-t border-black/15">
              {section.items.map((item, j) => (
                <CVItem key={j} item={item} />
              ))}
            </div>
          </motion.section>
        ))}
      </div>
    </main>
  );
}

function CVItem({ item }) {
  return (
    <div className="py-6 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-start">
      <div className="md:col-span-4">
        <h3 className="font-display font-bold text-xl md:text-2xl leading-tight">
          {item.role}
        </h3>
        {item.place && (
          <p className="mt-1 text-[color:var(--charcoal)] text-base">
            {item.place}
          </p>
        )}
      </div>
      <div className="md:col-span-3">
        {item.period && (
          <span className="inline-block text-sm font-medium text-[color:var(--pink)] tracking-wide">
            {item.period}
          </span>
        )}
      </div>
      <div className="md:col-span-5">
        {item.bullets && item.bullets.length > 0 && (
          <ul className="space-y-2">
            {item.bullets.map((b, k) => (
              <li
                key={k}
                className="text-base md:text-lg leading-relaxed flex gap-3 items-start"
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--pink)] mt-2.5 shrink-0"
                  aria-hidden="true"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
